/* eslint-disable react/jsx-indent */
import React, {useState} from 'react'
import {TextField, Autocomplete, Popper, useTheme} from '@mui/material'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import {getStationList, getStationListNew} from 'services/common.service'
import _ from 'lodash'
import {useSelector} from 'react-redux'
import {AI_CARRIER} from 'config/carriers.constant'

const getErrorMsg = (id: string, indexCount?: number) => {
    if (id === `manualBooking[${indexCount}].from` || id === `manualBooking[${indexCount}].to`) id = 'manualbooking'
    switch (id) {
        case 'from':
            return 'From field is not valid.'
        case 'airport':
            return 'Airport field is not valid.'
        case 'to':
            return 'To field is not valid.'
        case 'origin':
            return 'Origin field is not valid.'
        case 'destination':
            return 'Destination field is not valid.'
        case 'manualbooking':
            return 'Invalid'
        default:
            return 'Field is invalid'
    }
}

export const stationDetailsCache = {}

interface FormikStationAutocompleteProps {
    id: string
    className?: string
    formik: any
    renderOption?(...args: unknown[]): any
    message?: string
    disabled?: boolean
    readOnly?: boolean
    placeholder?: string
    isAutoPopulated?: boolean
    indexCount?: number
    handleBlur?(...args: unknown[]): unknown
    handleChange?(...args: unknown[]): unknown
}

const FormikStationAutocomplete = ({
    id,
    formik,
    renderOption,
    message,
    className,
    disabled,
    readOnly,
    placeholder,
    isAutoPopulated,
    indexCount,
    handleBlur,
    handleChange
}: FormikStationAutocompleteProps) => {
    const theme = useTheme()
    const [options, setOptions] = useState<any[]>([])
    const [isLoading, setLoading] = useState<boolean>(true)
    const [loadingText, setLoadingText] = useState<string | undefined>(message)
    const [blurVersion, setBlurVersion] = useState<boolean>(false)
    const [isPopulated, setAutoPopulated] = useState<boolean | undefined>(isAutoPopulated)
    const [error, setError] = useState<string>('')
    const station = useSelector((state: any) => state.login.loginDetails?.securityParams?.station)
    const selectedCarrier = useSelector((state: any) => state.bookingService?.selectedCarrier)
    const branchRelationshipParts = useSelector((state: any) => state.login.loginDetails?.branchRelationshipParts)

    const handler = (event) => {
        formik.setFieldTouched(id)
        if (event.target.value?.length === 3) {
            if (options.length > 0) {
                formik.setFieldValue(id, options[0])
                if (handleBlur) handleBlur(options[0])
                setError('')
            } else {
                formik.setFieldValue(id, {code: event.target.value})
                setError(getErrorMsg(id, indexCount))
            }
            return
        }
        if (blurVersion) {
            if (event.target.value?.length > 0) {
                if (options.length > 0) {
                    formik.setFieldValue(id, options[0])
                    if (handleBlur) handleBlur(options[0])
                    setError('')
                } else {
                    formik.setFieldValue(id, {code: event.target.value})
                    setError(getErrorMsg(id, indexCount))
                }
            }
        } else if (event.target.value?.length > 3) {
            formik.setFieldValue(id, {code: event.target.value})
            setError(getErrorMsg(id, indexCount))
        } else if (event.target.value?.length < 3 && event.target.value?.length > 0) {
            formik.setFieldValue(id, {code: event.target.value})
            setError('dontShow')
        } else {
            formik.setFieldValue(id, '')
            setError('')
        }
    }

    const changeHandler = (event) => {
        const value = event.target.value
        if (value.length === 3) {
            setError('')
        }
        formik.setFieldValue(id, {code: value})
    }

    const keyHandler = (event) => {
        const keyCode = event.keyCode || event.which
        const key = String.fromCharCode(keyCode)
        const alphabeticRegex = /[a-zA-Z \b\t\n]/
        if (!alphabeticRegex.test(key) && event.code !== 'ArrowLeft' && event.code !== 'ArrowRight') {
            event.preventDefault()
        }
        if (event.type === 'keydown' && event.key === 'Tab') {
            setBlurVersion(true)
        } else {
            setBlurVersion(false)
        }
    }

    const createFilterOptions = (inputValue: string, options: any[], id: string) => {
        if (options.length !== 0 && inputValue !== '0') {
            // added to show top results and other can be scrollable
            if (id === 'airport') {
                return options
            } else return options.slice(0, 10)
        } else {
            return []
        }
    }

    const getOptions = async (value: string) => {
        if (!value) return

        const stationCode = _.toUpper(value)

        if (isPopulated ?? stationDetailsCache[stationCode]) {
            setOptions(stationDetailsCache[stationCode])
            return
        }

        let data
        const dataCountry = branchRelationshipParts?.filter((carrier) => carrier.carrierCode === selectedCarrier)
        if (selectedCarrier === AI_CARRIER && dataCountry.length !== 0 && dataCountry[0].customerType === 'D') {
            const values = [value, station]
            data = await getStationListNew([...values])
        } else {
            data = await getStationList(value)
        }
        setOptions(data.data.response)
        stationDetailsCache[stationCode] = data.data.response

        if (value.length === 0) {
            setLoading(true)
            setLoadingText(message)
            setOptions([])
        } else {
            setLoading(false)
        }
        if (data?.data?.response?.length === 0) {
            formik.setFieldTouched(id)
            setLoading(true)
            setLoadingText('No Options')
            setError(getErrorMsg(id, indexCount))
        } else {
            setError('')
        }
    }

    return (
        <Autocomplete
            freeSolo
            autoComplete
            autoHighlight
            disableClearable
            id={id}
            // onBlur={handleBlur}
            onFocus={() => setAutoPopulated(false)}
            readOnly={readOnly}
            popupIcon=''
            loadingText={loadingText}
            loading={isLoading}
            options={options}
            getOptionLabel={(option) => option?.code ?? option}
            value={_.get(formik.values, id)}
            filterOptions={(options, {inputValue}) => createFilterOptions(inputValue, options, id)}
            className={className}
            onInputChange={(_, value) => {
                void getOptions(value)
            }}
            onChange={(event, value) => {
                if (value) {
                    if (handleChange) handleChange(value)
                    formik.setFieldValue(id, value)
                }
            }}
            componentsProps={{
                paper: {
                    sx: {
                        width: 'fit-content',
                        minWidth: 300,
                        overflow: 'auto'
                    }
                }
            }}
            ListboxProps={{
                sx: {maxHeight: '100%'}
            }}
            PopperComponent={({children, ...popperProps}) => (
                <Popper
                    {...popperProps}
                    sx={{
                        zIndex: theme.zIndex.modal + 1,
                        width: 'fit-content',
                        minWidth: '300px',
                        maxWidth: 'none',
                        maxHeight: '370px',
                        overflowY: 'auto',
                        border: options?.length > 0 || isLoading ? '1px solid #909090' : undefined,
                        boxShadow: '(35% 000, x0, y1, blur 4)',
                        borderBottomRightRadius: '5px',
                        borderBottomLeftRadius: '5px'
                    }}
                    placement='bottom-start'
                >
                    {children}
                </Popper>
            )}
            isOptionEqualToValue={(option, value) => option === value.code || option.code === value.code}
            renderOption={renderOption}
            disabled={disabled}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant='outlined'
                    value={_.get(formik.values, id)}
                    placeholder={placeholder}
                    onChange={changeHandler}
                    onBlur={handler}
                    onKeyDown={keyHandler}
                    InputProps={{
                        ...params.InputProps,
                        inputProps: {
                            ...params.inputProps,
                            maxLength: 6,
                            name: id,
                            id
                        }
                    }}
                    disabled={disabled}
                    error={error?.length > 0 || (_.get(formik.touched, id) && !!_.get(formik.errors, id))}
                    helperText={
                        _.get(formik.touched, id) &&
                        !!_.get(formik.errors, id) &&
                        error !== 'dontShow' && (
                            <>
                                <ErrorOutlineOutlinedIcon className='formik-error__info-icon' />
                                {error?.length > 0 ? error : _.get(formik.errors, id)}
                            </>
                        )
                    }
                />
            )}
        />
    )
}

FormikStationAutocomplete.defaultProps = {
    disabled: false,
    readOnly: false,
    placeholder: '',
    isAutoPopulated: false,
    indexCount: 0
}

export default FormikStationAutocomplete
